/**
 * Module-level cache for S3 resource fetches. Keyed by URL; stores the
 * in-flight Promise so concurrent callers share one network round-trip.
 *
 * Files served from S3 are immutable per deploy — we never invalidate.
 * If upstream content changes, a cache-busting query string would be
 * cleaner than runtime invalidation.
 */
const cache = new Map<string, Promise<unknown>>();

/**
 * Fetch and parse a resource exactly once per URL within the app's lifetime.
 * The `parse` callback receives the raw Response and must produce the typed
 * value (e.g., `r => r.json()`, `r => r.text().then(parseCsv)`).
 *
 * Throws if the response is not OK so callers can surface errors uniformly.
 */
export const fetchWithCache = <T>(
  url: string,
  parse: (response: Response) => Promise<T>,
): Promise<T> => {
  const cached = cache.get(url) as Promise<T> | undefined;
  if (cached) return cached;

  const promise = fetch(url)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return parse(res);
    })
    .catch(err => {
      cache.delete(url);
      throw err;
    });

  cache.set(url, promise);
  return promise;
};
